import React, { useState, useEffect, useRef } from "react";
import { Paper, Typography, Grid, TextField, Box, Chip } from "@mui/material";
import {
  isValidName,
  isValidEmail,
  isValidPhone,
  formatPhoneNumber,
} from "../../utils/validation";
import { useAuth } from "../../context/AuthContext";

const ContactInfoForm = ({ contactInfo, setContactInfo, setFormValid }) => {
  const { user, isAuthenticated } = useAuth();
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();

  useEffect(() => {
    if (
      user &&
      isAuthenticated &&
      user.profile.email !== "staff@cakeyhand.com"
    ) {
      setContactInfo((prev) => ({
        ...prev,
        firstName: user.profile.firstName || "",
        lastName: user.profile.lastName || "",
        email: user.profile.email || "",
        phone: user.profile.phoneNumber || "",
      }));
    }
  }, [user, isAuthenticated, setContactInfo]);

  const handleChange = (field, value) => {
    const updatedValue = field === "phone" ? formatPhoneNumber(value) : value;

    setContactInfo((prev) => ({
      ...prev,
      [field]: updatedValue,
    }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateAllFields();
  };

  const validateAllFields = () => {
    const errs = {};

    if (!isValidName(contactInfo.firstName))
      errs.firstName = "Enter a valid first name";
    if (!isValidName(contactInfo.lastName))
      errs.lastName = "Enter a valid last name";
    if (!isValidEmail(contactInfo.email)) errs.email = "Enter a valid email";
    if (!isValidPhone(contactInfo.phone))
      errs.phone = "Enter a valid 10-digit phone";

    setErrors(errs);
    setFormValid(Object.keys(errs).length === 0);
  };

  useEffect(() => {
    validateAllFields();
  }, [contactInfo]);

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={2}>
        <Typography size="subtitle1" fontWeight="bold">
          All fields are required
        </Typography>
        {Object.keys(errors).length === 0 && (
          <Chip label="✓ Valid" size="small" color="success" />
        )}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={6}>
          <TextField
            label="First Name"
            inputRef={firstNameRef}
            fullWidth
            value={contactInfo.firstName}
            onChange={(e) => handleChange("firstName", e.target.value)}
            onBlur={() => handleBlur("firstName")}
            error={touched.firstName && !!errors.firstName}
            helperText={touched.firstName && errors.firstName}
            onKeyDown={(e) => {
              if (/\d/.test(e.key)) e.preventDefault();
              else if (e.key === "Enter") lastNameRef.current?.focus();
            }}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            label="Last Name"
            inputRef={lastNameRef}
            fullWidth
            value={contactInfo.lastName}
            onChange={(e) => handleChange("lastName", e.target.value)}
            onBlur={() => handleBlur("lastName")}
            error={touched.lastName && !!errors.lastName}
            helperText={touched.lastName && errors.lastName}
            onKeyDown={(e) => {
              if (/\d/.test(e.key)) e.preventDefault();
              else if (e.key === "Enter") emailRef.current?.focus();
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Email"
            inputRef={emailRef}
            fullWidth
            value={contactInfo.email}
            onChange={(e) => handleChange("email", e.target.value)}
            onBlur={() => handleBlur("email")}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
            onKeyDown={(e) => {
              if (e.key === "Enter") phoneRef.current?.focus();
            }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label="Phone Number"
            inputRef={phoneRef}
            fullWidth
            value={contactInfo.phone}
            onChange={(e) => handleChange("phone", e.target.value)}
            onBlur={() => handleBlur("phone")}
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone}
            placeholder="(123) 456-7890"
          />
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ContactInfoForm;
